import { useEffect, useState } from 'react';
import type { TripDetailsData } from './Step1TripDetails';
import type { CustomerDetailsData } from './Step2CustomerDetails';
import type { VehiclePricingData } from './Step3VehiclePricing';
import { apiGetFleets } from '../../../api/services/fleetApi';
import type { FleetRecord } from '../../../types';
import { calculateTransferPrice } from '../../../../constants/transferBooking';
import { calculateHourlyPrice } from '../../../../constants/hourlyBooking';

type Step4Props = {
  tripData: TripDetailsData;
  customerData: CustomerDetailsData;
  vehicleData: VehiclePricingData;
};

export const Step4ReviewConfirm = ({ tripData, customerData, vehicleData }: Step4Props) => {
  const [vehicles, setVehicles] = useState<FleetRecord[]>([]);

  useEffect(() => {
    let mounted = true;
    apiGetFleets()
      .then((res) => {
        if (mounted && res?.data) {
          setVehicles(res.data);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const selectedVehicleEntries = Object.entries(vehicleData.selectedVehicles || {});
  
  const extrasPriceMap: Record<string, number> = {
    'infant-seat': 5,
    'child-seat': 5,
    'booster-seat': 0,
    'ski-snowboard': 0,
  };

  const extrasTotal = customerData.options.reduce((sum, option) => sum + (extrasPriceMap[option] || 0), 0);

  const getVehiclePrice = (v: FleetRecord): number => {
    const isTransfers =
      tripData.serviceType === 'Transfers' || tripData.serviceType === 'Transfer';
    const isHourly =
      tripData.serviceType === 'Hourly drivers' || tripData.serviceType === 'hourly';
    const isTwoWay = tripData.journeyType === 'round-trip';

    const basePrice = Number(v.basePrice ?? 0);
    const pricePerKm = Number(v.pricePerKm ?? 0);

    if (isHourly) {
      const dist =
        tripData.distanceKm ||
        (tripData.durationHours ? tripData.durationHours * 40 : 80);
      return calculateHourlyPrice(basePrice, pricePerKm, dist);
    }
    if (isTransfers) {
      const dist = tripData.distanceKm || 0;
      const oneWay = calculateTransferPrice(basePrice, pricePerKm, dist);
      return isTwoWay ? oneWay * 2 : oneWay;
    }
    return basePrice || 0;
  };
  
  const vehicleTotal = selectedVehicleEntries.reduce(
    (sum, [id, count]) => {
      const vehicle = vehicles.find(
        (candidate) => String(candidate.id) === String(id)
      );
      if (!vehicle) return sum;

      const bookingPrice =
        vehicleData.vehiclePriceOverrides?.[id] ??
        getVehiclePrice(vehicle);

      return sum + bookingPrice * count;
    },
    0
  );
  
  const grandTotal = vehicleTotal + extrasTotal;

  const isRoundTrip = tripData.journeyType === 'round-trip';

  return (
    <div className="flex flex-col gap-6 w-full max-w-[1000px] mx-auto px-[32px]">
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Left Column */}
        <div className="flex flex-col gap-6">
          
          {/* Booking Summary */}
          <div className="w-full rounded-[16px] border border-[#E0E0E0] bg-white p-6 relative">
            <h3 className="font-host text-[16px] font-bold text-[#1A1A1A] mb-5">Booking Summary</h3>
            
            {/* Booking ID Pill */}
            <div className="absolute top-6 right-6 flex flex-col items-end bg-[#84A9FF] rounded-[6px] px-3 py-1 text-white text-right">
              <span className="font-host text-[10px] font-medium leading-tight">Booking Mode</span>
              <span className="font-host text-[12px] font-bold leading-tight">Admin Booking</span>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Service</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {tripData.serviceType || 'Transfers'}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Source Name</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {tripData.sourceName || 'Not specified'}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Trip Type</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {tripData.journeyType === 'one-way' ? 'One Way' : 'Round Trip'}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Pickup Location</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A] max-w-[200px] truncate" title={tripData.pickupLocation}>
                  {tripData.pickupLocation || 'Not specified'}
                </span>
              </div>
              {tripData.serviceType !== 'Hourly drivers' && (
                <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                  <span className="font-host text-[13px] text-[#757575]">Drop-off Location</span>
                  <span className="font-host text-[13px] font-medium text-[#1A1A1A] max-w-[200px] truncate" title={tripData.dropoffLocation}>
                    {tripData.dropoffLocation || 'Not specified'}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Pickup Date</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {tripData.pickupDate ? new Date(tripData.pickupDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Not specified'}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Pickup Time</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {tripData.pickupTime || 'Not specified'}
                </span>
              </div>

              {isRoundTrip && (
                <>
                  <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                    <span className="font-host text-[13px] text-[#757575]">Return Date</span>
                    <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                      {tripData.returnDate ? new Date(tripData.returnDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Not specified'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                    <span className="font-host text-[13px] text-[#757575]">Return Time</span>
                    <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                      {tripData.returnTime || 'Not specified'}
                    </span>
                  </div>
                </>
              )}

              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Distance</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {tripData.distanceKm ? `${tripData.distanceKm} km` : '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-host text-[13px] text-[#757575]">Estimated Duration</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {tripData.durationText || (tripData.durationHours ? `${tripData.durationHours} hrs` : '—')}
                </span>
              </div>
            </div>
          </div>

          {/* Arrival Information */}
          <div className="w-full rounded-[16px] border border-[#E0E0E0] bg-white p-6">
            <h3 className="font-host text-[16px] font-bold text-[#1A1A1A] mb-5">Arrival Information</h3>
            
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">{customerData.arrivalType === 'flight' ? 'Airline' : 'Train Operator'}</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {customerData.airline === 'Search Airline' || !customerData.airline ? 'Not specified' : customerData.airline}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">{customerData.arrivalType === 'flight' ? 'Flight Number' : 'Train Number'}</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {customerData.flightNumber || 'Not specified'}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Arrival Terminal</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {customerData.arrivalTerminal || 'Not specified'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-host text-[13px] text-[#757575]">Scheduled Arrival Time</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {customerData.arrivalTime || 'Not specified'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-6">
          
          {/* Customer Details */}
          <div className="w-full rounded-[16px] border border-[#E0E0E0] bg-white p-6">
            <h3 className="font-host text-[16px] font-bold text-[#1A1A1A] mb-5">Customer Details</h3>
            
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Customer Name</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {customerData.firstName || customerData.lastName ? `${customerData.firstName} ${customerData.lastName}` : 'Not specified'}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Email</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {customerData.email || 'Not specified'}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Phone Number</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {customerData.phoneNumber || 'Not specified'}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Service Type</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {tripData.serviceType || 'Not specified'}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Passengers</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {tripData.passengers || '1'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-host text-[13px] text-[#757575]">Luggage</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  {tripData.luggage || '0'}
                </span>
              </div>
            </div>
          </div>

          {/* Pricing Summary */}
          <div className="w-full rounded-[16px] border border-[#E0E0E0] bg-white p-6">
            <h3 className="font-host text-[16px] font-bold text-[#1A1A1A] mb-5">Pricing Summary</h3>
            
            <div className="flex flex-col gap-4">
              {selectedVehicleEntries.length === 0 && (
                <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                  <span className="font-host text-[13px] text-[#757575]">Vehicle</span>
                  <span className="font-host text-[13px] font-medium text-[#1A1A1A]">None</span>
                </div>
              )}
              {selectedVehicleEntries.map(([id, count]) => {
                const v = vehicles.find((cand) => String(cand.id) === String(id));
                const unitPrice =
                  vehicleData.vehiclePriceOverrides?.[id] ??
                  (v ? getVehiclePrice(v) : 0);
                return (
                  <div key={id} className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                    <div className="flex flex-col">
                      <span className="font-host text-[13px] text-[#757575]">Vehicle</span>
                      <span className="font-host text-[11px] font-medium text-[#9E9E9E]">{v?.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-host text-[13px] font-medium text-[#1A1A1A]">{v?.name}</span>
                      <div className="px-2 py-0.5 rounded-[4px] bg-[#E0E0E0] text-[10px] font-bold font-host text-[#1A1A1A]">
                        €{unitPrice}
                      </div>
                      {count > 1 && (
                        <div className="px-2 py-0.5 rounded-[4px] bg-[#E0E0E0] text-[10px] font-bold font-host text-[#1A1A1A]">
                          x{count}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Payment Status</span>
                <span className="px-2 py-1 bg-[#FFDDC8] text-[#FF6A1A] text-[10px] font-bold font-host rounded-[4px]">
                  Pending
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Vehicle Fare</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  €{Math.round(vehicleTotal)}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-[#F5F5F5] pb-3">
                <span className="font-host text-[13px] text-[#757575]">Extras</span>
                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  €{Math.round(extrasTotal)}
                </span>
              </div>
            </div>

            <div className="mt-4 flex justify-between items-center bg-[#84A9FF] rounded-[8px] p-4">
              <span className="font-host text-[14px] font-bold text-white">Total Fare</span>
              <span className="font-host text-[18px] font-bold text-white">
                €{Math.round(grandTotal)}
              </span>
            </div>
          </div>
          
        </div>
        
      </div>
    </div>
  );
};
